export const name = "VECTOR_SEARCH";
export const summary = `Performs a vector search on embeddings to find semantically similar entities.`;
export const reference = `
## VECTOR_SEARCH

Performs a vector search on embeddings to find semantically similar entities.

\`\`\`
VECTOR_SEARCH(
  {TABLE base_table | base_table_query_statement},
  column_to_search,
  TABLE query_table
  [, query_column_to_search => query_column_to_search_value]
  [, top_k => top_k_value]
  [, distance_type => distance_type_value]
  [, options => options_value]
)
\`\`\`

### 

\`\`\`
VECTOR_SEARCH(
  { TABLE base_table | base_table_query_statement },
  column_to_search,
  (query_statement)
  [, [query_column_to_search =>] query_column_to_search_value]
  [, top_k => top_k_value]
  [, distance_type => distance_type_value]
  [, options => options_value]
)
\`\`\`


### Description

The \`VECTOR_SEARCH\` function lets you search embeddings to find semantically similar entities.

Embeddings are high-dimensional numerical vectors that represent a given entity, like a piece of text or an audio file. Machine learning (ML) models use embeddings to encode semantics about such entities to make it easier to reason about and compare them. For example, a common operation in clustering, classification, and recommendation models is to measure the distance between vectors in an embedding space to find items that are most semantically similar.


### Note: The
ML.DISTANCE function
is another vector function that calculates the distance between vectors. To
compare two specific vectors and determine the distance between them, use the
ML.DISTANCE function. To search a dataset for vectors similar
to an input vector, use the VECTOR_SEARCH function.




### Definitions

- \`base_table\`: The table to search for nearest neighbor embeddings.
- \`base_table_query_statement\`: A query that you can use to pre-filter the base table. Only \`SELECT\`, \`FROM\`, and \`WHERE\` clauses are allowed in this query. Don't apply any filters to the embedding column. You can't use logical views in this query. Using a subquery might interfere with index usage or cause your query to fail. If the base table is indexed and the \`WHERE\` clause contains columns that are not stored in the index, then \`VECTOR_SEARCH\` post-filters on those columns instead. To learn more, see Store columns and pre-filter.
- \`column_to_search\`: The name of the base table column to search for nearest neighbor embeddings. The column must have a type of \`ARRAY<FLOAT64>\`. All elements in the array must be non-\`NULL\`, and all values in the column must have the same array dimensions. If the column has a vector index, BigQuery attempts to use it. To determine if an index was used in the vector search, see Vector index usage.
- \`query_table\`: The table that provides the embeddings for which to find nearest neighbors. All columns are passed through as output columns.
- \`query_statement\`: A query that provides the embeddings for which to find nearest neighbors. All columns are passed through as output columns.
- \`query_column_to_search\`: A named argument with a \`STRING\` value.\`query_column_to_search_value\` specifies the name of the column in the query table or statement that contains the embeddings for which to find nearest neighbors. The column must have a type of \`ARRAY<FLOAT64>\`. All elements in the array must be non-\`NULL\`and all values in the column must have the same array dimensions as the values in the \`column_to_search\` column. If you don't specify \`query_column_to_search_value\`, the function uses the\`column_to_search\` value or picks the most appropriate column.
- \`top_k\`: A named argument with an \`INT64\` value. \`top_k_value\`specifies the number of nearest neighbors to return. The default is \`10\`. A negative value is treated as infinity, meaning that all values are counted as neighbors and returned.
- \`distance_type\`: A named argument with a \`STRING\` value.\`distance_type_value\` specifies the type of metric to use to compute the distance between two vectors. Supported distance types are \`EUCLIDEAN\`, \`COSINE\`, and \`DOT_PRODUCT\`. The default is \`EUCLIDEAN\`.

- \`options\`: A named argument with a JSON-formatted \`STRING\` value.\`options_value\` is a literal that specifies the following vector search options:


### Details

You can optionally use \`VECTOR_SEARCH\` with a vector index. When a vector index is used, \`VECTOR_SEARCH\` uses the Approximate Nearest Neighbor search technique to help improve vector search performance, with the trade-off of reducing recall and so returning more approximate results. Brute force is used to return exact results when a vector index isn't available, and you can choose to use brute force to get exact results even when a vector index is available.


### Output

For each row in the query data, the output contains multiple rows from the base table that satisfy the search criteria. The number of results rows per query table row is either 10 or the \`top_k\` value if it's specified. The order of the output isn't guaranteed.

The output includes the following columns:

- \`query\`: A \`STRUCT\` value that contains all selected columns from the query data.
- \`base\`: A \`STRUCT\` value that contains all columns from \`base_table\` or a subset of the columns from \`base_table\` that you selected in the\`base_table_query_statement\` query.
- \`distance\`: A \`FLOAT64\` value that represents the distance between the base data and the query data.


### Limitations

BigQuery data security and governance rules apply to the use of\`VECTOR_SEARCH\`, which results in the following behavior:

- If the base table has row-level security policies, \`VECTOR_SEARCH\` applies the row-level access policies to the query results.
- If the indexed column from the base table has data masking policies, \`VECTOR_SEARCH\` succeeds only if the user running the query has the \`Fine-Grained Reader\` role on the policy tags that are used. Otherwise, \`VECTOR_SEARCH\` fails with an invalid query error.
- If any base table column or any column in the query table or statement has column-level security policies and you don't have appropriate permissions to access the column, \`VECTOR_SEARCH\` fails with a permission denied error.

- The project that runs the query containing \`VECTOR_SEARCH\` must match the project that contains the base table.


### Examples

The following queries create test tables \`table1\` and \`table2\` to use in subsequent query examples :



\`\`\`
CREATE OR REPLACE TABLE mydataset.table1
(
  id INT64,
  my_embedding ARRAY<FLOAT64>
);

INSERT mydataset.table1 (id, my_embedding)
VALUES(1, [1.0, 2.0]),
(2, [2.0, 4.0]),
(3, [1.5, 7.0]),
(4, [1.0, 3.2]),
(5, [5.0, 5.4]),
(6, [3.7, 1.8]),
(7, [4.4, 2.9]);
\`\`\`



\`\`\`
CREATE OR REPLACE TABLE mydataset.table2
(
  query_id STRING,
  embedding ARRAY<FLOAT64>
);

INSERT mydataset.table2 (query_id, embedding)
VALUES('dog', [1.0, 2.0]),
('cat', [3.0, 5.2]);
\`\`\`

The following example searches the \`my_embedding\` column of \`table1\` for the top two embeddings that match each row of data in the \`embedding\` column of\`table2\`:



\`\`\`
SELECT *
FROM
  VECTOR_SEARCH(
    TABLE mydataset.table1,
    'my_embedding',
    (SELECT query_id, embedding FROM mydataset.table2),
    'embedding',
    top_k => 2);

/*----------------+-----------------+---------+----------------------------------------*
 | query.query_id | query.embedding | base.id | base.my_embedding | distance |
 +----------------+-----------------+---------+-------------------+--------------------+
 | dog | 1.0 | 1 | 1.0 | 0 |
 | | 2.0 | | 2.0 | |
 +----------------+-----------------+---------+-------------------+--------------------+
 | dog | 1.0 | 4 | 1.0 | 1.2000000000000002 |
 | | 2.0 | | 3.2 | |
 +----------------+-----------------+---------+-------------------+--------------------+
 | cat | 3.0 | 2 | 2.0 | 1.5620499351813311 |
 | | 5.2 | | 4.0 | |
 +----------------+-----------------+---------+-------------------+--------------------+
 | cat | 3.0 | 5 | 5.0 | 2.0099751242241779 |
 | | 5.2 | | 5.4 | |
 *----------------+-----------------+---------+-------------------+--------------------*/
\`\`\`

The following example pre-filters \`table1\` to rows where \`id\` isn't equal to 4 and then searches the \`my_embedding\` column of \`table1\` for the top two embeddings that match each row of data in the \`embedding\` column of\`table2\`.



\`\`\`
SELECT *
FROM
  VECTOR_SEARCH(
    (SELECT * FROM mydataset.table1 WHERE id != 4),
    'my_embedding',
    (SELECT query_id, embedding FROM mydataset.table2),
    'embedding',
    top_k => 2,
    options => '{"use_brute_force":true}');

/*----------------+-----------------+---------+----------------------------------------*
 | query.query_id | query.embedding | base.id | base.my_embedding | distance |
 +----------------+-----------------+---------+-------------------+--------------------+
 | dog | 1.0 | 1 | 1.0 | 0 |
 | | 2.0 | | 2.0 | |
 +----------------+-----------------+---------+-------------------+--------------------+
 | dog | 1.0 | 2 | 2.0 | 2.23606797749979 |
 | | 2.0 | | 4.0 | |
 +----------------+-----------------+---------+-------------------+--------------------+
 | cat | 3.0 | 2 | 2.0 | 1.5620499351813311 |
 | | 5.2 | | 4.0 | |
 +----------------+-----------------+---------+-------------------+--------------------+
 | cat | 3.0 | 5 | 5.0 | 2.0099751242241779 |
 | | 5.2 | | 5.4 | |
 *----------------+-----------------+---------+-------------------+--------------------*/
\`\`\`

The following example searches the \`my_embedding\` column of \`table1\` for the top two embeddings that match each row of data in the \`embedding\` column of\`table2\`, and uses the \`COSINE\` distance type to measure the distance between the embeddings:



\`\`\`
SELECT *
FROM
  VECTOR_SEARCH(
    TABLE mydataset.table1,
    'my_embedding',
    TABLE mydataset.table2,
    'embedding',
    top_k => 2,
    distance_type => 'COSINE');

/*----------------+-----------------+---------+-------------------------------------------+
 | query.query_id | query.embedding | base.id | base.my_embedding | distance |
 +----------------+-----------------+---------+-------------------+-----------------------+
 | dog | 1.0 | 2 | 2.0 | 0 |
 | | 2.0 | | 4.0 | |
 +----------------+-----------------+---------+-------------------+-----------------------+
 | dog | 1.0 | 1 | 1.0 | 0 |
 | | 2.0 | | 2.0 | |
 +----------------+-----------------+---------+-------------------+-----------------------+
 | cat | 3.0 | 2 | 2.0 | 0.0017773842088002478 |
 | | 5.2 | | 4.0 | |
 +----------------+-----------------+---------+-------------------+-----------------------+
 | cat | 3.0 | 1 | 1.0 | 0.0017773842088002478 |
 | | 5.2 | | 2.0 | |
 *----------------+-----------------+---------+-------------------+-----------------------*/
\`\`\`




source: https://cloud.google.com/bigquery/docs/reference/standard-sql/search_functions
`;
